/**
 * WebSocket Client for FIAS Platform Streaming
 *
 * Provides real-time token streaming for AI responses.
 * Handles connection management, reconnection, and message routing.
 */

/** Server → Client message types */
interface TokenMessage {
  type: 'token';
  nodeId: string;
  text: string;
  requestId: string;
}

interface ThinkingMessage {
  type: 'thinking';
  nodeId: string;
  text: string;
  requestId: string;
}

interface NodeCompleteMessage {
  type: 'node_complete';
  nodeId: string;
  requestId: string;
}

interface CompleteMessage {
  type: 'complete';
  requestId: string;
  /** Extra payload fields (e.g., guidedResult) passed through to onComplete */
  [key: string]: unknown;
}

interface ErrorMessage {
  type: 'error';
  code: string;
  message: string;
}

interface SubscribedMessage {
  type: 'subscribed';
  requestId: string;
}

interface UnsubscribedMessage {
  type: 'unsubscribed';
}

interface PongMessage {
  type: 'pong';
}

/** Image generation progress message */
interface ProgressMessage {
  type: 'progress';
  stage: string;
  message: string;
  requestId: string;
}

/** Image generation complete message */
interface ImageCompleteMessage {
  type: 'image_complete';
  requestId: string;
  imageRef: string;
  fileId?: string;
  revisedPrompt?: string;
  provider: string;
  model: string;
}

interface UiUpdateMessage {
  type: 'ui_update';
  nodeId: string;
  requestId: string;
  uiDefinition: unknown;
}

interface SettingsUpdateMessage {
  type: 'settings_update';
  requestId: string;
  settings: Record<string, unknown>;
}

interface FilesUpdateMessage {
  type: 'files_update';
  requestId: string;
  files: Record<string, unknown>;
}

/** Verbose mode message types */
interface SystemPromptMessage {
  type: 'system_prompt';
  requestId: string;
  prompt: string;
}

interface ToolCallMessage {
  type: 'tool_call';
  requestId: string;
  toolName: string;
  toolInput: unknown;
}

interface ToolResultMessage {
  type: 'tool_result';
  requestId: string;
  toolName: string;
  toolResult: unknown;
  isError: boolean;
}

interface ModelInfoMessage {
  type: 'model_info';
  requestId: string;
  model: string;
  intent: string;
}

interface ToolsAvailableMessage {
  type: 'tools_available';
  requestId: string;
  tools: Array<{ name: string; description: string }>;
}

/**
 * Chat message types from server.
 * These are intentional duplicates of the canonical types in @fias/kv-types.
 * The client package cannot depend on the server-side kv-types package directly.
 * If modifying these, also update: packages/kv-types/src/websocket.ts
 */
interface ChatMessageEventData {
  type: 'chat_message';
  channelId: string;
  message: {
    messageId: string;
    senderId: string;
    senderName: string | null;
    content: string;
    messageType: string;
    replyToId: string | null;
    attachmentRefs: Array<{
      fileId: string;
      fileName: string;
      mimeType: string;
      sizeBytes: number;
    }> | null;
    mentions: Array<{ userId: string; displayName: string }> | null;
    createdAt: string;
  };
}

interface ChatMessageEditedEventData {
  type: 'chat_message_edited';
  channelId: string;
  messageId: string;
  content: string;
  editedAt: string;
}

interface ChatMessageDeletedEventData {
  type: 'chat_message_deleted';
  channelId: string;
  messageId: string;
}

interface ChatTypingEventData {
  type: 'chat_typing';
  channelId: string;
  userId: string;
  displayName: string;
}

interface ChatReactionEventData {
  type: 'chat_reaction';
  channelId: string;
  messageId: string;
  userId: string;
  emoji: string;
  action: 'add' | 'remove';
}

interface ChatPresenceEventData {
  type: 'chat_presence';
  userId: string;
  status: 'online' | 'offline' | 'away';
}

interface ChatChannelAddedEventData {
  type: 'chat_channel_added';
  channelId: string;
}

interface ChatSubscribedEventData {
  type: 'chat_subscribed';
  channelIds: string[];
}

/** Fias AI search results message */
interface SearchResultsMessage {
  type: 'search_results';
  requestId: string;
  results: unknown[];
  query: string;
}

/** Fias AI arche proposal message */
interface ArcheProposalMessage {
  type: 'arche_proposal';
  requestId: string;
  proposalId: string;
  actions: Array<{
    actionId: string;
    params: Record<string, unknown>;
    description: string;
  }>;
}

type ServerMessage =
  | TokenMessage
  | ThinkingMessage
  | NodeCompleteMessage
  | CompleteMessage
  | ErrorMessage
  | SubscribedMessage
  | UnsubscribedMessage
  | PongMessage
  | ProgressMessage
  | ImageCompleteMessage
  | UiUpdateMessage
  | SettingsUpdateMessage
  | FilesUpdateMessage
  | SystemPromptMessage
  | ToolCallMessage
  | ToolResultMessage
  | ModelInfoMessage
  | ToolsAvailableMessage
  | ChatMessageEventData
  | ChatMessageEditedEventData
  | ChatMessageDeletedEventData
  | ChatTypingEventData
  | ChatReactionEventData
  | ChatPresenceEventData
  | ChatChannelAddedEventData
  | ChatSubscribedEventData
  | SearchResultsMessage
  | ArcheProposalMessage;

/** Client → Server message types */
interface SubscribeClientMessage {
  type: 'subscribe';
  requestId: string;
}

interface UnsubscribeClientMessage {
  type: 'unsubscribe';
}

interface PingClientMessage {
  type: 'ping';
}

interface ChatSubscribeClientMessage {
  type: 'chat_subscribe';
  channelIds: string[];
}

interface ChatUnsubscribeClientMessage {
  type: 'chat_unsubscribe';
  channelIds: string[];
}

interface ChatTypingClientMessage {
  type: 'chat_typing';
  channelId: string;
}

interface ChatReadClientMessage {
  type: 'chat_read';
  channelId: string;
  messageId: string;
}

type ClientMessage =
  | SubscribeClientMessage
  | UnsubscribeClientMessage
  | PingClientMessage
  | ChatSubscribeClientMessage
  | ChatUnsubscribeClientMessage
  | ChatTypingClientMessage
  | ChatReadClientMessage;

/** Connection state */
type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/** Event callbacks */
type TokenCallback = (nodeId: string, text: string, requestId: string) => void;
type ThinkingCallback = (nodeId: string, text: string, requestId: string) => void;
type NodeCompleteCallback = (nodeId: string, requestId: string) => void;
type RequestCompleteCallback = (requestId: string, payload?: Record<string, unknown>) => void;
type ErrorCallback = (code: string, message: string) => void;
type ConnectionChangeCallback = (state: ConnectionState) => void;
type SubscriptionCallback = (requestId: string) => void;
type ProgressCallback = (stage: string, message: string, requestId: string) => void;
type ImageCompleteCallback = (data: {
  requestId: string;
  imageRef: string;
  fileId?: string;
  revisedPrompt?: string;
  provider: string;
  model: string;
}) => void;
type UiUpdateCallback = (nodeId: string, uiDefinition: unknown, requestId: string) => void;
type SettingsUpdateCallback = (settings: Record<string, unknown>, requestId: string) => void;
type FilesUpdateCallback = (files: Record<string, unknown>, requestId: string) => void;
type SystemPromptCallback = (prompt: string, requestId: string) => void;
type ToolCallCallback = (toolName: string, toolInput: unknown, requestId: string) => void;
type ToolResultCallback = (
  toolName: string,
  toolResult: unknown,
  isError: boolean,
  requestId: string,
) => void;
type ModelInfoCallback = (model: string, intent: string, requestId: string) => void;
type ToolsAvailableCallback = (
  tools: Array<{ name: string; description: string }>,
  requestId: string,
) => void;

/** Chat event callbacks */
type ChatMessageCallback = (channelId: string, message: ChatMessageEventData['message']) => void;
type ChatMessageEditedCallback = (
  channelId: string,
  messageId: string,
  content: string,
  editedAt: string,
) => void;
type ChatMessageDeletedCallback = (channelId: string, messageId: string) => void;
type ChatTypingCallback = (channelId: string, userId: string, displayName: string) => void;
type ChatReactionCallback = (
  channelId: string,
  messageId: string,
  userId: string,
  emoji: string,
  action: 'add' | 'remove',
) => void;
type ChatPresenceCallback = (userId: string, status: 'online' | 'offline' | 'away') => void;
type ChatChannelAddedCallback = (channelId: string) => void;

/** Fias AI event callbacks */
type SearchResultsCallback = (results: unknown[], query: string, requestId: string) => void;
type ArcheProposalCallback = (
  proposal: {
    proposalId: string;
    actions: Array<{ actionId: string; params: Record<string, unknown>; description: string }>;
  },
  requestId: string,
) => void;

interface WebSocketConfig {
  /** WebSocket endpoint URL (without protocol) */
  endpoint: string;
  /** Maximum reconnection attempts */
  maxReconnectAttempts?: number;
  /** Base reconnection delay in ms */
  reconnectDelayMs?: number;
  /** Heartbeat interval in ms */
  heartbeatIntervalMs?: number;
}

const DEFAULT_MAX_RECONNECT_ATTEMPTS = 5;
const DEFAULT_RECONNECT_DELAY_MS = 1000;
const DEFAULT_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * FIAS WebSocket Client
 *
 * Usage:
 * ```typescript
 * const ws = new FiasWebSocket({ endpoint: 'wss://api.example.com/stream' });
 *
 * // Set up event handlers
 * ws.onToken((nodeId, text) => console.log(`${nodeId}: ${text}`));
 * ws.onNodeComplete((nodeId) => console.log(`Node ${nodeId} complete`));
 * ws.onComplete((requestId) => console.log(`Request ${requestId} complete`));
 *
 * // Connect with a ticket provider — fresh single-use ticket per (re)connect
 * await ws.connect(() => authApi.getWebSocketTicket());
 *
 * // Subscribe to a request
 * ws.subscribe(requestId);
 *
 * // Later, disconnect
 * ws.disconnect();
 * ```
 */
export class FiasWebSocket {
  private config: Required<WebSocketConfig>;
  private socket: WebSocket | null = null;
  private state: ConnectionState = 'disconnected';
  // Provider is invoked on every (re)connect inside `doConnect()` so
  // each connection gets a fresh single-use ws-ticket. Required because
  // ws-tickets are consumed atomically on connect — replaying one is a
  // hard fail.
  private tokenProvider: (() => Promise<string | null>) | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private currentSubscription: string | null = null;
  private chatSubscribedChannels = new Set<string>();
  private reconnectCallbacks: Array<() => void> = [];

  // Event callbacks
  private tokenCallbacks: TokenCallback[] = [];
  private thinkingCallbacks: ThinkingCallback[] = [];
  private nodeCompleteCallbacks: NodeCompleteCallback[] = [];
  private completeCallbacks: RequestCompleteCallback[] = [];
  private errorCallbacks: ErrorCallback[] = [];
  private connectionChangeCallbacks: ConnectionChangeCallback[] = [];
  private subscriptionCallbacks: SubscriptionCallback[] = [];
  private progressCallbacks: ProgressCallback[] = [];
  private imageCompleteCallbacks: ImageCompleteCallback[] = [];
  private uiUpdateCallbacks: UiUpdateCallback[] = [];
  private settingsUpdateCallbacks: SettingsUpdateCallback[] = [];
  private filesUpdateCallbacks: FilesUpdateCallback[] = [];
  private systemPromptCallbacks: SystemPromptCallback[] = [];
  private toolCallCallbacks: ToolCallCallback[] = [];
  private toolResultCallbacks: ToolResultCallback[] = [];
  private modelInfoCallbacks: ModelInfoCallback[] = [];
  private toolsAvailableCallbacks: ToolsAvailableCallback[] = [];
  private chatMessageCallbacks: ChatMessageCallback[] = [];
  private chatMessageEditedCallbacks: ChatMessageEditedCallback[] = [];
  private chatMessageDeletedCallbacks: ChatMessageDeletedCallback[] = [];
  private chatTypingCallbacks: ChatTypingCallback[] = [];
  private chatReactionCallbacks: ChatReactionCallback[] = [];
  private chatPresenceCallbacks: ChatPresenceCallback[] = [];
  private chatChannelAddedCallbacks: ChatChannelAddedCallback[] = [];
  private searchResultsCallbacks: SearchResultsCallback[] = [];
  private archeProposalCallbacks: ArcheProposalCallback[] = [];

  constructor(config: WebSocketConfig) {
    this.config = {
      ...config,
      maxReconnectAttempts: config.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS,
      reconnectDelayMs: config.reconnectDelayMs ?? DEFAULT_RECONNECT_DELAY_MS,
      heartbeatIntervalMs: config.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS,
    };
  }

  /**
   * Gets the current connection state
   */
  getState(): ConnectionState {
    return this.state;
  }

  /**
   * Checks if connected
   */
  isConnected(): boolean {
    return this.state === 'connected';
  }

  /**
   * Gets the current subscription (if any)
   */
  getCurrentSubscription(): string | null {
    return this.currentSubscription;
  }

  /**
   * Connects to the WebSocket server.
   *
   * The provider is invoked on every (re)connect and must return a
   * fresh single-use ws-ticket from `authApi.getWebSocketTicket()`.
   * The ticket is sent as `?ticket=` on the WebSocket URL.
   */
  async connect(tokenProvider: () => Promise<string | null>): Promise<void> {
    if (this.state === 'connected' || this.state === 'connecting') {
      return;
    }

    this.tokenProvider = tokenProvider;
    this.reconnectAttempts = 0;

    return this.doConnect();
  }

  /**
   * Subscribes to streaming updates for a request
   * @param requestId The request ID to subscribe to
   */
  subscribe(requestId: string): void {
    if (this.state !== 'connected') {
      console.warn('Cannot subscribe: WebSocket not connected');
      return;
    }

    this.currentSubscription = requestId;
    this.send({ type: 'subscribe', requestId });
  }

  /**
   * Unsubscribes from streaming updates
   */
  unsubscribe(): void {
    if (this.state !== 'connected') {
      return;
    }

    this.currentSubscription = null;
    this.send({ type: 'unsubscribe' });
  }

  /**
   * Disconnects from the WebSocket server
   */
  disconnect(): void {
    this.stopReconnect();
    this.stopHeartbeat();
    this.currentSubscription = null;

    if (this.socket) {
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }

    this.setState('disconnected');
  }

  /**
   * Registers a callback for token events
   */
  onToken(callback: TokenCallback): () => void {
    this.tokenCallbacks.push(callback);
    return () => {
      this.tokenCallbacks = this.tokenCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for thinking events (extended thinking from models like Claude)
   */
  onThinking(callback: ThinkingCallback): () => void {
    this.thinkingCallbacks.push(callback);
    return () => {
      this.thinkingCallbacks = this.thinkingCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for node complete events
   */
  onNodeComplete(callback: NodeCompleteCallback): () => void {
    this.nodeCompleteCallbacks.push(callback);
    return () => {
      this.nodeCompleteCallbacks = this.nodeCompleteCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for request complete events
   */
  onComplete(callback: RequestCompleteCallback): () => void {
    this.completeCallbacks.push(callback);
    return () => {
      this.completeCallbacks = this.completeCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for error events
   */
  onError(callback: ErrorCallback): () => void {
    this.errorCallbacks.push(callback);
    return () => {
      this.errorCallbacks = this.errorCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for connection state changes
   */
  onConnectionChange(callback: ConnectionChangeCallback): () => void {
    this.connectionChangeCallbacks.push(callback);
    return () => {
      this.connectionChangeCallbacks = this.connectionChangeCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /**
   * Registers a callback for subscription confirmations
   */
  onSubscribed(callback: SubscriptionCallback): () => void {
    this.subscriptionCallbacks.push(callback);
    return () => {
      this.subscriptionCallbacks = this.subscriptionCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for progress events (used by image generation)
   */
  onProgress(callback: ProgressCallback): () => void {
    this.progressCallbacks.push(callback);
    return () => {
      this.progressCallbacks = this.progressCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for image complete events
   */
  onImageComplete(callback: ImageCompleteCallback): () => void {
    this.imageCompleteCallbacks.push(callback);
    return () => {
      this.imageCompleteCallbacks = this.imageCompleteCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for UI definition updates (from builder tools)
   */
  onUiUpdate(callback: UiUpdateCallback): () => void {
    this.uiUpdateCallbacks.push(callback);
    return () => {
      this.uiUpdateCallbacks = this.uiUpdateCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for settings updates (e.g. guided phase changes)
   */
  onSettingsUpdate(callback: SettingsUpdateCallback): () => void {
    this.settingsUpdateCallbacks.push(callback);
    return () => {
      this.settingsUpdateCallbacks = this.settingsUpdateCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for file updates (from plugin builder tools)
   */
  onFilesUpdate(callback: FilesUpdateCallback): () => void {
    this.filesUpdateCallbacks.push(callback);
    return () => {
      this.filesUpdateCallbacks = this.filesUpdateCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for system prompt events (verbose mode)
   */
  onSystemPrompt(callback: SystemPromptCallback): () => void {
    this.systemPromptCallbacks.push(callback);
    return () => {
      this.systemPromptCallbacks = this.systemPromptCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for tool call events (verbose mode)
   */
  onToolCall(callback: ToolCallCallback): () => void {
    this.toolCallCallbacks.push(callback);
    return () => {
      this.toolCallCallbacks = this.toolCallCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for tool result events (verbose mode)
   */
  onToolResult(callback: ToolResultCallback): () => void {
    this.toolResultCallbacks.push(callback);
    return () => {
      this.toolResultCallbacks = this.toolResultCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for model info events (verbose mode)
   */
  onModelInfo(callback: ModelInfoCallback): () => void {
    this.modelInfoCallbacks.push(callback);
    return () => {
      this.modelInfoCallbacks = this.modelInfoCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for tools available events (verbose mode)
   */
  onToolsAvailable(callback: ToolsAvailableCallback): () => void {
    this.toolsAvailableCallbacks.push(callback);
    return () => {
      this.toolsAvailableCallbacks = this.toolsAvailableCallbacks.filter((cb) => cb !== callback);
    };
  }

  // ==========================================================================
  // Chat Methods
  // ==========================================================================

  /**
   * Subscribes to chat channels for real-time updates
   */
  subscribeToChatChannels(channelIds: string[]): void {
    if (this.state !== 'connected') {
      console.warn('Cannot subscribe to chat channels: WebSocket not connected');
      return;
    }
    channelIds.forEach((id) => this.chatSubscribedChannels.add(id));
    this.send({ type: 'chat_subscribe', channelIds });
  }

  /**
   * Unsubscribes from chat channels
   */
  unsubscribeFromChatChannels(channelIds: string[]): void {
    channelIds.forEach((id) => this.chatSubscribedChannels.delete(id));
    if (this.state !== 'connected') return;
    this.send({ type: 'chat_unsubscribe', channelIds });
  }

  /**
   * Registers a callback invoked after a WebSocket reconnection completes.
   */
  onReconnect(callback: () => void): () => void {
    this.reconnectCallbacks.push(callback);
    return () => {
      this.reconnectCallbacks = this.reconnectCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Sends a typing indicator for a chat channel
   */
  sendChatTyping(channelId: string): void {
    if (this.state !== 'connected') return;
    this.send({ type: 'chat_typing', channelId });
  }

  /**
   * Sends a read receipt for a chat channel
   */
  sendChatRead(channelId: string, messageId: string): void {
    if (this.state !== 'connected') return;
    this.send({ type: 'chat_read', channelId, messageId });
  }

  /**
   * Registers a callback for new chat messages
   */
  onChatMessage(callback: ChatMessageCallback): () => void {
    this.chatMessageCallbacks.push(callback);
    return () => {
      this.chatMessageCallbacks = this.chatMessageCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for edited chat messages
   */
  onChatMessageEdited(callback: ChatMessageEditedCallback): () => void {
    this.chatMessageEditedCallbacks.push(callback);
    return () => {
      this.chatMessageEditedCallbacks = this.chatMessageEditedCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /**
   * Registers a callback for deleted chat messages
   */
  onChatMessageDeleted(callback: ChatMessageDeletedCallback): () => void {
    this.chatMessageDeletedCallbacks.push(callback);
    return () => {
      this.chatMessageDeletedCallbacks = this.chatMessageDeletedCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /**
   * Registers a callback for typing indicators
   */
  onChatTyping(callback: ChatTypingCallback): () => void {
    this.chatTypingCallbacks.push(callback);
    return () => {
      this.chatTypingCallbacks = this.chatTypingCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for reaction changes
   */
  onChatReaction(callback: ChatReactionCallback): () => void {
    this.chatReactionCallbacks.push(callback);
    return () => {
      this.chatReactionCallbacks = this.chatReactionCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for presence changes
   */
  onChatPresence(callback: ChatPresenceCallback): () => void {
    this.chatPresenceCallbacks.push(callback);
    return () => {
      this.chatPresenceCallbacks = this.chatPresenceCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for when the user is added to a new channel
   */
  onChatChannelAdded(callback: ChatChannelAddedCallback): () => void {
    this.chatChannelAddedCallbacks.push(callback);
    return () => {
      this.chatChannelAddedCallbacks = this.chatChannelAddedCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /**
   * Registers a callback for Fias AI search results
   */
  onSearchResults(callback: SearchResultsCallback): () => void {
    this.searchResultsCallbacks.push(callback);
    return () => {
      this.searchResultsCallbacks = this.searchResultsCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for Fias AI arche proposals
   */
  onArcheProposal(callback: ArcheProposalCallback): () => void {
    this.archeProposalCallbacks.push(callback);
    return () => {
      this.archeProposalCallbacks = this.archeProposalCallbacks.filter((cb) => cb !== callback);
    };
  }

  // Private methods

  private async doConnect(): Promise<void> {
    this.setState('connecting');

    // Mint a fresh single-use ticket. Required on every (re)connect
    // because the previous ticket has been consumed by the prior
    // connect attempt and cannot be replayed.
    if (!this.tokenProvider) {
      this.setState('disconnected');
      throw new Error('No token provider configured — call connect() first');
    }
    let ticket: string | null;
    try {
      ticket = await this.tokenProvider();
    } catch (err) {
      // The provider may throw for non-auth failures (e.g. 5xx from the
      // ws-ticket endpoint). Reset state so a follow-up connect() isn't
      // blocked by `state === 'connecting'`, then re-throw verbatim —
      // useStreaming relies on the original message to decide whether
      // this is a session-expired event vs. a transient outage.
      this.setState('disconnected');
      throw err;
    }
    if (!ticket) {
      this.setState('disconnected');
      throw new Error('Authentication credential unavailable');
    }

    return new Promise((resolve, reject) => {
      const wsUrl = `${this.config.endpoint}?ticket=${encodeURIComponent(ticket)}`;

      try {
        this.socket = new WebSocket(wsUrl);

        this.socket.onopen = () => {
          this.setState('connected');
          this.reconnectAttempts = 0;
          this.startHeartbeat();

          // Re-subscribe if we had a subscription before reconnect
          if (this.currentSubscription) {
            this.subscribe(this.currentSubscription);
          }

          // Re-subscribe to chat channels after reconnect
          if (this.chatSubscribedChannels.size > 0) {
            this.send({
              type: 'chat_subscribe',
              channelIds: Array.from(this.chatSubscribedChannels),
            });
          }

          // Notify reconnect listeners
          this.reconnectCallbacks.forEach((cb) => cb());

          resolve();
        };

        this.socket.onclose = (event) => {
          this.socket = null;
          this.stopHeartbeat();

          if (event.code === 1000) {
            // Normal close
            this.setState('disconnected');
          } else {
            // Abnormal close - try to reconnect
            this.scheduleReconnect();
          }
        };

        this.socket.onerror = (error) => {
          console.error('WebSocket error:', error);
          if (this.state === 'connecting') {
            reject(new Error('Failed to connect to WebSocket'));
          }
        };

        this.socket.onmessage = (event) => {
          this.handleMessage(event.data);
        };
      } catch (error) {
        this.setState('disconnected');
        reject(error);
      }
    });
  }

  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data) as ServerMessage;

      switch (message.type) {
        case 'token':
          this.tokenCallbacks.forEach((cb) => cb(message.nodeId, message.text, message.requestId));
          break;

        case 'thinking':
          this.thinkingCallbacks.forEach((cb) =>
            cb(message.nodeId, message.text, message.requestId),
          );
          break;

        case 'node_complete':
          this.nodeCompleteCallbacks.forEach((cb) => cb(message.nodeId, message.requestId));
          break;

        case 'complete': {
          // Extract extra payload fields (everything except type/requestId)
          const { type: _type, requestId: completedId, ...payload } = message;
          const extra =
            Object.keys(payload).length > 0 ? (payload as Record<string, unknown>) : undefined;
          this.completeCallbacks.forEach((cb) => cb(completedId, extra));
          break;
        }

        case 'error':
          this.errorCallbacks.forEach((cb) => cb(message.code, message.message));
          break;

        case 'subscribed':
          this.subscriptionCallbacks.forEach((cb) => cb(message.requestId));
          break;

        case 'unsubscribed':
          // No callbacks needed for unsubscribe confirmation
          break;

        case 'pong':
          // Heartbeat response - no action needed
          break;

        case 'progress':
          this.progressCallbacks.forEach((cb) =>
            cb(message.stage, message.message, message.requestId),
          );
          break;

        case 'image_complete':
          this.imageCompleteCallbacks.forEach((cb) =>
            cb({
              requestId: message.requestId,
              imageRef: message.imageRef,
              fileId: message.fileId,
              revisedPrompt: message.revisedPrompt,
              provider: message.provider,
              model: message.model,
            }),
          );
          break;

        case 'ui_update':
          this.uiUpdateCallbacks.forEach((cb) =>
            cb(message.nodeId, message.uiDefinition, message.requestId),
          );
          break;

        case 'settings_update':
          this.settingsUpdateCallbacks.forEach((cb) => cb(message.settings, message.requestId));
          break;

        case 'files_update':
          this.filesUpdateCallbacks.forEach((cb) => cb(message.files, message.requestId));
          break;

        case 'system_prompt':
          this.systemPromptCallbacks.forEach((cb) => cb(message.prompt, message.requestId));
          break;

        case 'tool_call':
          this.toolCallCallbacks.forEach((cb) =>
            cb(message.toolName, message.toolInput, message.requestId),
          );
          break;

        case 'tool_result':
          this.toolResultCallbacks.forEach((cb) =>
            cb(message.toolName, message.toolResult, message.isError, message.requestId),
          );
          break;

        case 'model_info':
          this.modelInfoCallbacks.forEach((cb) =>
            cb(message.model, message.intent, message.requestId),
          );
          break;

        case 'tools_available':
          this.toolsAvailableCallbacks.forEach((cb) => cb(message.tools, message.requestId));
          break;

        case 'chat_message':
          this.chatMessageCallbacks.forEach((cb) => cb(message.channelId, message.message));
          break;

        case 'chat_message_edited':
          this.chatMessageEditedCallbacks.forEach((cb) =>
            cb(message.channelId, message.messageId, message.content, message.editedAt),
          );
          break;

        case 'chat_message_deleted':
          this.chatMessageDeletedCallbacks.forEach((cb) =>
            cb(message.channelId, message.messageId),
          );
          break;

        case 'chat_typing':
          this.chatTypingCallbacks.forEach((cb) =>
            cb(message.channelId, message.userId, message.displayName),
          );
          break;

        case 'chat_reaction':
          this.chatReactionCallbacks.forEach((cb) =>
            cb(message.channelId, message.messageId, message.userId, message.emoji, message.action),
          );
          break;

        case 'chat_presence':
          this.chatPresenceCallbacks.forEach((cb) => cb(message.userId, message.status));
          break;

        case 'chat_channel_added':
          this.chatChannelAddedCallbacks.forEach((cb) => cb(message.channelId));
          break;

        case 'chat_subscribed':
          // Acknowledgment of chat channel subscription
          break;

        case 'search_results':
          this.searchResultsCallbacks.forEach((cb) =>
            cb(message.results, message.query, message.requestId),
          );
          break;

        case 'arche_proposal':
          this.archeProposalCallbacks.forEach((cb) =>
            cb({ proposalId: message.proposalId, actions: message.actions }, message.requestId),
          );
          break;

        default:
          console.warn('Unknown WebSocket message type:', (message as { type: string }).type);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  private send(message: ClientMessage): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  private setState(state: ConnectionState): void {
    if (this.state !== state) {
      this.state = state;
      this.connectionChangeCallbacks.forEach((cb) => cb(state));
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping' });
    }, this.config.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.setState('disconnected');
      this.errorCallbacks.forEach((cb) =>
        cb('CONNECTION_FAILED', 'Failed to reconnect after maximum attempts'),
      );
      return;
    }

    this.setState('reconnecting');
    this.reconnectAttempts++;

    // Exponential backoff with jitter
    const delay = Math.min(
      this.config.reconnectDelayMs * Math.pow(2, this.reconnectAttempts - 1) + Math.random() * 1000,
      30000, // Max 30 seconds
    );

    console.log(`Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.doConnect().catch((error) => {
        console.error('Reconnection failed:', error);
        this.scheduleReconnect();
      });
    }, delay);
  }

  private stopReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
  }
}

// Singleton instance with default config
let defaultInstance: FiasWebSocket | null = null;

/**
 * Gets or creates the default WebSocket instance
 * @param endpoint WebSocket endpoint URL
 */
export function getWebSocket(endpoint?: string): FiasWebSocket {
  if (!defaultInstance && endpoint) {
    defaultInstance = new FiasWebSocket({ endpoint });
  }
  if (!defaultInstance) {
    throw new Error('WebSocket not initialized. Call getWebSocket with endpoint first.');
  }
  return defaultInstance;
}

/**
 * Resets the default WebSocket instance
 * Used for testing or when switching environments
 */
export function resetWebSocket(): void {
  if (defaultInstance) {
    defaultInstance.disconnect();
    defaultInstance = null;
  }
}

export default FiasWebSocket;
